import { useMemo, KeyboardEvent, ReactElement, PropsWithChildren, Children, isValidElement, AriaAttributes } from 'react';

import { RadioCardGroupContextValue, RadioCardGroupContext } from './RadioCardGroupContext';

interface Props extends PropsWithChildren {
   value: string;
   ariaLabelledBy?: AriaAttributes['aria-labelledby'];
   onChange: (newValue: string) => void;
}

const hasValue = (element: unknown): element is { value: string } => {
   return !!element && typeof element === 'object' && 'value' in element && typeof element.value === 'string';
};

export const RadioCardGroup = ({ value, ariaLabelledBy, onChange, children }: Props) => {
   const validChildren = useMemo(() => {
      const childrenWithValue: ReactElement<{ value: string }>[] = [];
      Children.forEach(children, (element) => {
         if (!isValidElement(element)) return;
         if (!hasValue(element.props)) return;
         childrenWithValue.push(element as ReactElement<{ value: string }>);
      });
      return childrenWithValue;
   }, [children]);

   const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
      const selectedIndex = validChildren.findIndex((child) => child.props.value === value);

      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
         event.preventDefault();
         let newIndex = selectedIndex - 1;
         if (newIndex < 0) newIndex = validChildren.length - 1;
         const newValue = validChildren[newIndex].props.value;
         if (newValue === value) return;
         onChange(newValue);
      }
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
         event.preventDefault();
         let newIndex = selectedIndex + 1;
         if (newIndex > validChildren.length - 1) newIndex = 0;
         const newValue = validChildren[newIndex].props.value;
         if (newValue === value) return;
         onChange(newValue);
      }
   };

   const state: RadioCardGroupContextValue = useMemo(() => ({ value, onChange }), [value, onChange]);

   return (
      <RadioCardGroupContext value={state}>
         <div role="radiogroup" aria-labelledby={ariaLabelledBy} tabIndex={0} onKeyDown={handleKeyDown} className="gap-component-layout-box-spacing-gap-xs group flex flex-col items-stretch outline-none">
            {children}
         </div>
      </RadioCardGroupContext>
   );
};
